import { DeleteButton } from '@mm/components';
import { getDeviceInfo } from '@mm/hooks';
import { AngleLeftLightIcon, EditIcon } from '@mm/icons';
import { usePromoterCRUDActions } from '@mm/state/actions/PromoterCRUDActions';
import {
  helpLinks,
  mapPromoterLinkTypeToUIFilterKind,
} from '@mm/state/helpers/chat-starters-helpers';
import PageModel from '@mm/state/model/PageModel';
import { PromoterModel } from '@mm/state/model/PromoterModel';
import modalActions from '@mm/state/modules/modalsState/actions';
import { activeFacebookPageSelector } from '@mm/state/selectors/facebookPageSelector';
import { activeChatbotIdSelector } from '@mm/state/selectors/viewSelectors';
import { StoreState } from '@mm/state/store-interfaces';
import classnames from 'classnames';
import queryString from 'query-string';
import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Dispatch } from 'redux';
import useRouter from 'use-react-router';
import {
  ButtonColor,
  PrimaryButton,
} from '../../../../../../common/buttons/PrimaryButton/PrimaryButton';
import { GreenMarker } from '../../../../../../common/components/GreenMarker/GreenMarker';
import TextInput from '../../../../../../common/components/TextInput';
import { BotBuilderRoutes } from '../../../../../../routes/bot-builder-routes';
import { NeedHelpLabel } from '../../../../common/components/NeedHelpLabel/NeedHelpLabel';
import { SwitchStatus } from '../../../../common/components/SwitchStatus/SwitchStatus';
import { useStartBotDetails } from '../../use-startbot-details';
import './EditStartBotHeader.pcss';

type Props = {
  promoter: PromoterModel;
  page: PageModel;
  botIsTemplate: boolean;
  customPreviewAction?: () => void;
  customActivateAction?: () => void;
  showGreenMarker?: boolean;
  customPreviewButtonLabel?: string;
  allowDelete?: boolean;
  css?: {
    header?: string;
    titleArea?: string;
  };
};

export const EditStartBotHeader = (props: Props) => {
  const {
    promoter,
    page,
    botIsTemplate,
    customPreviewAction,
    customActivateAction,
    showGreenMarker,
    customPreviewButtonLabel,
    allowDelete = true,
    css,
  } = props;
  const facebookPage = useSelector(activeFacebookPageSelector);
  const dispatch: Dispatch<StoreState> = useDispatch();
  const promoterCRUDActions = usePromoterCRUDActions();
  const chatbotId = useSelector(activeChatbotIdSelector);
  const session = useSelector((state: StoreState) => state.session.session);
  const { history } = useRouter();
  const [nameEditMode, setNameEditMode] = useState(false);
  const [localName, setLocalName] = useState(promoter.name);
  const startBotDetails = useStartBotDetails(promoter.type);
  const { isMobile } = getDeviceInfo();
  const localNameInvalid = !(localName ?? '').trim().length;

  const classList = classnames('edit-start-bot-header__name-input', {
    'edit-start-bot-header--name-disabled-mode': !nameEditMode,
  });

  const onClickActivate = () => {
    if (customActivateAction) {
      return customActivateAction();
    }
    promoterCRUDActions.update(chatbotId, {
      ...promoter,
      active: !promoter.active,
    });
  };

  const onClickPreview = () => {
    if (customPreviewAction) {
      return customPreviewAction();
    }
    if (facebookPage) {
      window.open(
        `http://m.me/${facebookPage.remoteId}?ref=${page.searchToken}_${session.searchToken}`,
      );
    }
  };
  const kind = mapPromoterLinkTypeToUIFilterKind(promoter.type);

  const onClickDelete = () => {
    const deleteCallback = async () => {
      await promoterCRUDActions.delete(promoter);

      const pathname = BotBuilderRoutes.chatStarters.getLink({ chatbotId });

      history.push({
        pathname,
        search: queryString.stringify({ kind }),
      });
    };

    dispatch(
      modalActions.openDeleteConfirmModal({
        headerText: `Are you sure you want to delete this ${startBotDetails?.singularPromoterName}?`,
        deleteCallback,
      }),
    );
  };

  const onClickCancelSaveName = () => {
    setLocalName(promoter.name);
    setNameEditMode(false);
  };

  const onClickSaveName = () => {
    promoterCRUDActions.update(chatbotId, {
      ...promoter,
      name: localName,
    });

    setNameEditMode(false);
  };

  const link = helpLinks[promoter.type];
  return (
    <div className={classnames('edit-start-bot-header', css?.header)}>
      <div className="edit-start-bot-header__title-area-container">
        <div
          className="edit-start-bot-header__back-button"
          onClick={() =>
            history.push(
              BotBuilderRoutes.chatStarters.getLink({
                chatbotId,
                kind,
              }),
            )
          }
        >
          <AngleLeftLightIcon />
          Back
        </div>
        <div
          className={classnames(
            'edit-start-bot-header__title-area',
            css?.titleArea,
          )}
        >
          <div className="edit-start-bot-header__title-area-left">
            {nameEditMode ? (
              <TextInput
                value={localName}
                onChange={e => setLocalName(e.target.value)}
                disabled={!nameEditMode}
                className={classList}
                maxLength={50}
              />
            ) : (
              <div className="edit-start-bot-header__name-preview">
                {localName}
              </div>
            )}

            {!nameEditMode && (
              <EditIcon
                className="edit-start-bot-header__edit-icon"
                onClick={() => setNameEditMode(true)}
              />
            )}

            {nameEditMode && (
              <>
                <PrimaryButton
                  color={ButtonColor.invertedRed}
                  onClick={onClickCancelSaveName}
                  className="edit-start-bot-header__name-save-button"
                >
                  Cancel
                </PrimaryButton>

                <PrimaryButton
                  onClick={onClickSaveName}
                  className="edit-start-bot-header__name-save-button"
                  disabled={localNameInvalid}
                >
                  Save
                </PrimaryButton>
              </>
            )}
          </div>

          <div className="edit-start-bot-header__title-area-right">
            {link && <NeedHelpLabel link={link} iconOnly={isMobile} />}

            <div
              className={
                showGreenMarker
                  ? 'edit-start-bot-header__switch-and-green-marker--wrapper'
                  : ''
              }
            >
              <SwitchStatus
                active={promoter.active}
                disabled={botIsTemplate}
                onChange={onClickActivate}
              />

              {showGreenMarker && (
                <GreenMarker className="edit-start-bot-header__green-marker" />
              )}
            </div>

            <PrimaryButton
              color={ButtonColor.secondaryBlue}
              className="edit-start-bot-header__preview-button"
              onClick={onClickPreview}
              size={!isMobile ? 'lg' : 'md'}
            >
              {customPreviewButtonLabel || 'Preview'}
            </PrimaryButton>

            {allowDelete && <DeleteButton size="lg" onClick={onClickDelete} />}
          </div>
        </div>
      </div>
    </div>
  );
};
